import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, ChevronRight, FileText, Download, ArrowLeft, Mail, Phone, Plus, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { productImage } from '../utils/imageUtils';
import SEO, { generateBreadcrumbSchema, generateProductSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { t, language } = useLanguage();
  const { addToQuote, isInQuote } = useQuote();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [addedToQuote, setAddedToQuote] = useState(false);
  const [related, setRelated] = useState({ accessories: [], similar: [] });
  const [certifications, setCertifications] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, catRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/categories/tree`)
        ]);
        setProduct(productRes.data);
        setCategories(catRes.data);
        
        // Check if a specific variant was requested via URL
        const variantParam = searchParams.get('variant');
        
        if (productRes.data.variants?.length > 0) {
          if (variantParam) {
            // Find the variant matching the artikelnummer from URL
            const matchedVariant = productRes.data.variants.find(v => 
              String(v.Artikelnummer || v.artikelnummer || '') === variantParam
            );
            setSelectedVariant(matchedVariant || productRes.data.variants[0]);
          } else {
            setSelectedVariant(productRes.data.variants[0]);
          }
        }

        // Fetch related products (accessories + similar) using the resolved id/slug
        const productKey = productRes.data.slug || productRes.data.id || id;
        try {
          const relRes = await axios.get(`${API}/products/${productKey}/related?limit=8`);
          setRelated(relRes.data || { accessories: [], similar: [] });
        } catch {
          setRelated({ accessories: [], similar: [] });
        }

        // Fetch certifications referenced by this product
        const certIds = productRes.data.certification_ids || [];
        if (certIds.length > 0) {
          try {
            const allCertsRes = await axios.get(`${API}/certifications?active_only=true`);
            const map = Object.fromEntries((allCertsRes.data || []).map((c) => [c.id, c]));
            setCertifications(certIds.map((cid) => map[cid]).filter(Boolean));
          } catch {
            setCertifications([]);
          }
        } else {
          setCertifications([]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, searchParams]);

  if (loading) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-stone-200 rounded w-1/3 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-stone-200" />
              <div className="space-y-4">
                <div className="h-6 bg-stone-200 rounded w-1/2" />
                <div className="h-4 bg-stone-200 rounded w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-600">{language === 'sv' ? 'Produkt hittades inte' : 'Product not found'}</p>
          <Link to="/produkter">
            <Button variant="outline" className="mt-4 border-stone-300 text-stone-700 rounded-none">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const productName = language === 'sv' ? product.name_sv : product.name_en;
  const productDescription = language === 'sv' ? product.description_sv : product.description_en;
  const variantsList = product.variants || [];
  const documentsList = product.documents || [];
  
  // Get main category and subcategory names AND slugs
  const mainCatId = product.main_category_id || product.category;
  const subCatId = product.sub_category_id;
  
  let mainCategoryName = '';
  let mainCategorySlug = '';
  let subCategoryName = '';
  let subCategorySlug = '';
  
  for (const cat of categories) {
    if (cat.id === mainCatId || cat.slug === mainCatId) {
      mainCategoryName = language === 'sv' ? cat.name_sv : cat.name_en;
      mainCategorySlug = cat.slug || cat.id;
      if (subCatId) {
        for (const sub of cat.children || []) {
          if (sub.id === subCatId || sub.slug === subCatId) {
            subCategoryName = language === 'sv' ? sub.name_sv : sub.name_en;
            subCategorySlug = sub.slug || sub.id;
            break;
          }
        }
      }
      break;
    }
  }

  const productSlug = product.slug || product.id;

  return (
    <div data-testid="product-detail-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={`${productName} | ${mainCategoryName || 'VVS-produkt'}`}
        description={language === 'sv'
          ? `${productName} från Flowingo AB. ${productDescription?.replace(/<[^>]*>/g, '').slice(0, 120)}... Begär offert för ${mainCategoryName || 'VVS-produkter'}.`
          : `${productName} from Flowingo AB. ${productDescription?.replace(/<[^>]*>/g, '').slice(0, 120)}... Request a quote for ${mainCategoryName || 'HVAC products'}.`}
        language={language}
        type="product"
        image={product.image_url}
        canonical={`https://flowingo.se/produkter/${productSlug}`}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Produkter' : 'Products', url: 'https://flowingo.se/produkter' },
            ...(mainCategoryName ? [{ name: mainCategoryName, url: `https://flowingo.se/produkter/kategori/${mainCategorySlug}` }] : []),
            ...(subCategoryName ? [{ name: subCategoryName, url: `https://flowingo.se/produkter/kategori/${mainCategorySlug}/${subCategorySlug}` }] : []),
            { name: productName, url: `https://flowingo.se/produkter/${productSlug}` },
          ]),
          generateProductSchema(product, language, subCategoryName ? `${mainCategoryName} / ${subCategoryName}` : mainCategoryName),
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#321a27]">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
          <Link to="/produkter" className="hover:text-[#321a27]">{t('nav.products')}</Link>
          {mainCategoryName && (
            <>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
              <Link to={`/produkter/kategori/${mainCategorySlug}`} className="hover:text-[#321a27]">
                {mainCategoryName}
              </Link>
            </>
          )}
          {subCategoryName && (
            <>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
              <Link to={`/produkter/kategori/${mainCategorySlug}/${subCategorySlug}`} className="hover:text-[#321a27]">
                {subCategoryName}
              </Link>
            </>
          )}
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
          <span className="text-stone-900" aria-current="page">{productName}</span>
        </nav>

        {/* Product Header */}
        <div className="bg-white border border-stone-200 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left - Images */}
            <div className="p-6 border-r border-stone-200">
              <div className="aspect-square bg-white overflow-hidden mb-4">
                {product.image_url ? (
                  <img 
                    src={productImage(product.image_url)} 
                    alt={productName} 
                    width="600" height="600"
                    className="w-full h-full object-contain" 
                    data-testid="product-image" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <Package className="w-32 h-32" />
                  </div>
                )}
              </div>
              {/* Thumbnail row */}
              <div className="flex gap-2">
                <div className="w-16 h-16 border-2 border-[#321a27] bg-white flex items-center justify-center">
                  {product.image_url ? (
                    <img 
                      src={productImage(product.image_url)} 
                      alt={productName} 
                      width="64" height="64"
                      className="w-full h-full object-contain" 
                    />
                  ) : (
                    <Package className="w-8 h-8 text-stone-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Right - Info */}
            <div className="p-6">
              <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">
                {mainCategoryName}{subCategoryName && ` / ${subCategoryName}`}
              </div>
              
              <h1 data-testid="product-name" className="text-2xl font-bold text-stone-900 mb-4">
                {productName}
              </h1>

              {/* Add to Quote Button */}
              <div className="mb-6">
                <Button 
                  onClick={() => {
                    addToQuote(product, selectedVariant);
                    setAddedToQuote(true);
                    toast.success(language === 'sv' ? 'Tillagd i offertlistan!' : 'Added to quote list!');
                    setTimeout(() => setAddedToQuote(false), 2000);
                  }}
                  className={`rounded-none px-6 py-3 flex items-center gap-2 ${
                    addedToQuote 
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                      : 'bg-[#321a27] hover:bg-[#4a2d3d] text-white'
                  }`}
                  data-testid="add-to-quote-btn"
                >
                  {addedToQuote ? (
                    <>
                      <Check className="w-5 h-5" />
                      {language === 'sv' ? 'Tillagd!' : 'Added!'}
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      {language === 'sv' ? 'Lägg till i offertlista' : 'Add to quote list'}
                    </>
                  )}
                </Button>
              </div>

              {/* Variant count */}
              {variantsList.length > 1 && (
                <div className="mb-6">
                  <span className="text-sm text-stone-600">
                    {language === 'sv' ? `${variantsList.length} varianter tillgängliga` : `${variantsList.length} variants available`}
                  </span>
                </div>
              )}

              {/* Meta description - from Excel import */}
              {(product.meta_sv || product.meta_en) && (
                <div className="mb-6">
                  <p className="text-stone-700 leading-relaxed text-base">
                    {language === 'sv' ? product.meta_sv : product.meta_en}
                  </p>
                </div>
              )}

              {/* Technical Data - Moved here */}
              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-semibold text-stone-900 mb-3">
                  {language === 'sv' ? 'Teknisk data' : 'Technical data'}
                </h3>
                
                {selectedVariant && (
                  <div className="space-y-2">
                    {Object.entries(selectedVariant).map(([key, value]) => (
                      value !== undefined && value !== null && value !== '' && (
                        <div key={key} className="flex justify-between py-1 border-b border-stone-100 text-sm">
                          <span className="text-stone-500">{key.replace(/_/g, ' ')}</span>
                          <span className="text-stone-900 text-right">{String(value)}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Artikelinformation Section */}
        {productDescription && (
          <div className="bg-white border border-stone-200 mb-8">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
              <h2 className="font-semibold text-stone-900">
                {language === 'sv' ? 'Artikelinformation' : 'Article Information'}
              </h2>
            </div>
            <div className="p-6">
              <div 
                className="prose prose-stone max-w-none text-stone-700"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(productDescription || '') }}
              />
            </div>
          </div>
        )}

        {/* Tabs Section */}
        <div className="bg-white border border-stone-200">
          <Tabs defaultValue="variants" className="w-full">
            <TabsList className="w-full justify-start border-b border-stone-200 bg-transparent p-0 rounded-none h-auto">
              <TabsTrigger 
                value="variants" 
                className="px-6 py-4 text-sm font-medium data-[state=active]:text-[#321a27] data-[state=active]:border-b-2 data-[state=active]:border-[#321a27] rounded-none bg-transparent"
              >
                {language === 'sv' ? 'Varianter' : 'Variants'} ({variantsList.length})
              </TabsTrigger>
              <TabsTrigger 
                value="documents" 
                className="px-6 py-4 text-sm font-medium data-[state=active]:text-[#321a27] data-[state=active]:border-b-2 data-[state=active]:border-[#321a27] rounded-none bg-transparent"
              >
                {language === 'sv' ? 'Dokument' : 'Documents'} ({documentsList.length})
              </TabsTrigger>
            </TabsList>

            {/* Variants Tab */}
            <TabsContent value="variants" className="p-0">
              {variantsList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#321a27] text-white">
                      <tr>
                        {(product.variant_columns || Object.keys(variantsList[0] || {})).map((col) => (
                          <th key={col} className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                            {col.replace(/_/g, ' ')}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right text-sm font-medium whitespace-nowrap">
                          {language === 'sv' ? 'Lägg till' : 'Add'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {variantsList.map((variant, idx) => {
                        const isSelected = selectedVariant && 
                          (variant.Artikelnummer || variant.artikelnummer) === 
                          (selectedVariant.Artikelnummer || selectedVariant.artikelnummer);
                        
                        return (
                          <tr 
                            key={idx} 
                            data-testid={`variant-row-${idx}`}
                            onClick={() => setSelectedVariant(variant)}
                            className={`border-b border-stone-200 hover:bg-[#f5f0f2] cursor-pointer ${
                              isSelected 
                                ? 'bg-[#fef9e7] border-l-4 border-l-[#321a27]' 
                                : (idx % 2 === 0 ? 'bg-white' : 'bg-stone-50')
                            }`}
                          >
                            {(product.variant_columns || Object.keys(variantsList[0] || {})).map((col) => (
                              <td key={col} className={`px-4 py-3 text-sm ${isSelected ? 'font-semibold text-stone-900' : 'text-stone-700'}`}>
                                {variant[col] !== undefined ? String(variant[col]) : '-'}
                              </td>
                            ))}
                            <td className="px-4 py-3 text-right">
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToQuote(product, variant);
                                  toast.success(language === 'sv' ? 'Tillagd i offertlistan!' : 'Added to quote list!');
                                }}
                                className="bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none text-xs px-3 py-1 h-auto"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {language === 'sv' ? 'Offertlista' : 'Quote'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center text-stone-500">
                  {language === 'sv' ? 'Inga varianter tillgängliga' : 'No variants available'}
                </div>
              )}
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="p-6">
              {documentsList.length > 0 ? (
                <div className="space-y-2">
                  {documentsList.map((doc, idx) => {
                    // Handle both old format (string) and new format (object)
                    const docUrl = typeof doc === 'string' ? doc : doc.url;
                    const docName = typeof doc === 'string' 
                      ? doc.split('/').pop() 
                      : (doc.name || doc.original_filename || `Document ${idx + 1}`);
                    const fullUrl = docUrl.startsWith('/') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${docUrl}` 
                      : docUrl;
                    
                    // Add #view=FitH to Cloudinary URLs to suggest inline viewing
                    const viewUrl = fullUrl.includes('cloudinary.com') ? `${fullUrl}#view=FitH` : fullUrl;
                    
                    return (
                      <a
                        key={idx}
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 border border-stone-200 hover:bg-stone-50 transition-colors group"
                      >
                        <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <span className="text-stone-900 font-medium block">{docName}</span>
                          <span className="text-stone-500 text-sm">PDF</span>
                        </div>
                        <Button variant="outline" size="sm" className="border-stone-300 rounded-none opacity-0 group-hover:opacity-100 transition-opacity">
                          <Download className="w-4 h-4 mr-2" />
                          {language === 'sv' ? 'Öppna' : 'Open'}
                        </Button>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-stone-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{language === 'sv' ? 'Inga dokument tillgängliga' : 'No documents available'}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Certifications — Klassificeringar & märkningar */}
        <CertificationsRow certifications={certifications} language={language} />

        {/* Related products widget — Tillbehör (admin-curated) + Liknande produkter (auto) */}
        <RelatedProductsSection
          accessories={related.accessories}
          similar={related.similar}
          language={language}
        />
      </div>
    </div>
  );
};

// Compact product card used in the related-products widget
const RelatedProductCard = ({ product, language }) => {
  const { addToQuote } = useQuote();
  const name = language === 'sv' ? product.name_sv : product.name_en;
  const imgSrc = product.image_url ? productImage(product.image_url) : null;
  const variantCount = product.variants?.length || 0;
  const firstVariant = product.variants?.[0];

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!firstVariant) return;
    addToQuote({
      productId: product.id,
      productName: name,
      productSlug: product.slug || product.id,
      variant: firstVariant,
      image: product.image_url,
    });
    toast.success(language === 'sv' ? 'Tillagd i offertlista' : 'Added to quote list');
  };

  return (
    <Link
      to={`/produkter/${product.slug || product.id}`}
      data-testid={`related-product-${product.id}`}
      className="group flex flex-col bg-white border border-stone-200 hover:border-[#321a27] transition-colors"
    >
      <div className="aspect-square bg-stone-50 flex items-center justify-center overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <Package className="w-12 h-12 text-stone-300" />
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h4 className="text-sm font-semibold text-stone-900 line-clamp-2 group-hover:text-[#321a27]">{name}</h4>
        {variantCount > 0 && (
          <p className="text-xs text-stone-500 mt-1">
            {variantCount} {language === 'sv' ? 'varianter' : 'variants'}
          </p>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
          <span className="text-xs text-[#321a27] font-medium group-hover:underline">
            {language === 'sv' ? 'Visa' : 'View'} →
          </span>
          {firstVariant && (
            <button
              type="button"
              onClick={handleQuickAdd}
              data-testid={`related-quick-add-${product.id}`}
              className="text-xs flex items-center gap-1 px-2 py-1 border border-stone-300 hover:border-[#321a27] hover:text-[#321a27] transition-colors"
              title={language === 'sv' ? 'Lägg till första variant i offertlistan' : 'Add first variant to quote list'}
            >
              <Plus className="w-3 h-3" />
              {language === 'sv' ? 'Offert' : 'Quote'}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

const RelatedProductsSection = ({ accessories, similar, language }) => {
  const hasAccessories = accessories && accessories.length > 0;
  const hasSimilar = similar && similar.length > 0;
  if (!hasAccessories && !hasSimilar) return null;

  return (
    <div className="mt-16 space-y-12" data-testid="related-products-section">
      {hasAccessories && (
        <section>
          <div className="flex items-end justify-between mb-6 pb-4 border-b border-stone-200">
            <div>
              <h2 className="text-2xl font-semibold text-stone-900">
                {language === 'sv' ? 'Tillbehör & relaterade produkter' : 'Accessories & related products'}
              </h2>
              <p className="text-sm text-stone-500 mt-1">
                {language === 'sv'
                  ? 'Rekommenderade tillbehör som passar denna produkt.'
                  : 'Recommended accessories that fit this product.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="accessories-grid">
            {accessories.map((p) => (
              <RelatedProductCard key={p.id} product={p} language={language} />
            ))}
          </div>
        </section>
      )}

      {hasSimilar && (
        <section>
          <div className="flex items-end justify-between mb-6 pb-4 border-b border-stone-200">
            <div>
              <h2 className="text-2xl font-semibold text-stone-900">
                {language === 'sv' ? 'Liknande produkter' : 'Similar products'}
              </h2>
              <p className="text-sm text-stone-500 mt-1">
                {language === 'sv'
                  ? 'Andra produkter i samma kategori som du kanske är intresserad av.'
                  : 'Other products in the same category you may be interested in.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="similar-grid">
            {similar.map((p) => (
              <RelatedProductCard key={p.id} product={p} language={language} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const CertificationsRow = ({ certifications, language }) => {
  if (!certifications || certifications.length === 0) return null;
  return (
    <section className="mt-12 pt-8 border-t border-stone-200" data-testid="product-certifications-section">
      <h2 className="text-2xl font-semibold text-stone-900 mb-2">
        {language === 'sv' ? 'Klassificeringar & märkningar' : 'Certifications & markings'}
      </h2>
      <p className="text-sm text-stone-500 mb-6">
        {language === 'sv'
          ? 'Denna produkt är certifierad och godkänd enligt följande standarder.'
          : 'This product is certified according to the following standards.'}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6" data-testid="product-certifications-grid">
        {certifications.map((c) => {
          const inner = (
            <div
              key={c.id}
              data-testid={`product-cert-${c.id}`}
              title={c.description || c.name}
              className="group relative flex flex-col items-center justify-center p-4 bg-white border border-stone-200 hover:border-[#321a27] transition-colors"
            >
              <div className="h-16 w-full flex items-center justify-center">
                <img
                  src={c.logo_url}
                  alt={c.name}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                  style={{ background: 'transparent' }}
                />
              </div>
              <p className="mt-3 text-xs font-medium text-stone-700 text-center">{c.name}</p>
              {c.description && (
                <div
                  data-testid={`cert-tooltip-${c.id}`}
                  className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity bg-stone-900 text-white text-xs px-3 py-2 max-w-[240px] z-10 whitespace-normal text-center shadow-lg"
                >
                  {c.description}
                </div>
              )}
            </div>
          );
          return c.website ? (
            <a
              key={c.id}
              href={c.website}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              title={c.description || c.name}
            >
              {inner}
            </a>
          ) : inner;
        })}
      </div>
    </section>
  );
};

export default ProductDetailPage;
